import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Tab, Alert, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Tabs from '../components/UIKit/Tabs';
import UserManagementPanel from '../components/admin-dashboard/users/UserManagementPanel';
import AchievementManagement from './AchievementManagement';
import BadgeManagement from './BadgeManagement';
import ApiTokenManager from '../components/ApiTokenManager';
import OnboardingManager from '../components/admin-dashboard/onboarding/OnboardingManager';
import DatabaseBackupPanel from '../components/admin/DatabaseBackupPanel';
import ProfessionManagement from '../components/admin/ProfessionManagement';
import LessonChangeRequestsPage from './LessonChangeRequestsPage';
import CourseProgressDashboard from '../components/admin-dashboard/progress/CourseProgressDashboard';

const AdminDashboard = ({ initialTab = 0 }) => {
  const [tabValue, setTabValue] = useState(initialTab);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  
  // Check admin rights
  useEffect(() => {
    if (!user || !user.userMeta.roles.includes('admin')) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (initialTab) {
      setTabValue(initialTab);
    }
  }, [initialTab]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!user || !user.userMeta.roles.includes('admin')) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{t('common:errors.accessDenied')}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {t('admin:title')}
      </Typography>
      <Box
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: '4px' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="admin tabs"
            swapBackground
          >
            <Tab
              label={t('admin:tabs.users')}
              id="tab-0"
              aria-controls="tabpanel-0"
            />
            <Tab
              label={t('admin:tabs.achievements')}
              id="tab-1"
              aria-controls="tabpanel-1"
            />
            <Tab
              label={t('admin:tabs.badges')}
              id="tab-2"
              aria-controls="tabpanel-2"
            />
            <Tab
              label={t('admin:tabs.apiTokens')}
              id="tab-3"
              aria-controls="tabpanel-3"
            />
            <Tab
              label={t('admin:tabs.onboarding')}
              id="tab-4"
              aria-controls="tabpanel-4"
            />
            <Tab
              label={t('admin:tabs.database')}
              id="tab-5"
              aria-controls="tabpanel-5"
            />
            <Tab
              label={t('admin:tabs.professions')}
              id="tab-6"
              aria-controls="tabpanel-6"
            />
            <Tab
              label={t('admin:tabs.lessonChangeRequests')}
              id="tab-7"
              aria-controls="tabpanel-7"
            />
            <Tab
              label={t('admin:tabs.courseProgress')}
              id="tab-8"
              aria-controls="tabpanel-8"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box hidden={tabValue !== 0} id="tabpanel-0" aria-labelledby="tab-0">
            {tabValue === 0 && <UserManagementPanel />}
          </Box>
          <Box hidden={tabValue !== 1} id="tabpanel-1" aria-labelledby="tab-1">
            {tabValue === 1 && <AchievementManagement />}
          </Box>
          <Box hidden={tabValue !== 2} id="tabpanel-2" aria-labelledby="tab-2">
            {tabValue === 2 && <BadgeManagement />}
          </Box>
          <Box hidden={tabValue !== 3} id="tabpanel-3" aria-labelledby="tab-3">
            {tabValue === 3 && <ApiTokenManager />}
          </Box>
          <Box hidden={tabValue !== 4} id="tabpanel-4" aria-labelledby="tab-4">
            {tabValue === 4 && <OnboardingManager />}
          </Box>
          <Box hidden={tabValue !== 5} id="tabpanel-5" aria-labelledby="tab-5">
            {tabValue === 5 && <DatabaseBackupPanel />}
          </Box>
          <Box hidden={tabValue !== 6} id="tabpanel-6" aria-labelledby="tab-6">
            {tabValue === 6 && <ProfessionManagement />}
          </Box>
          <Box hidden={tabValue !== 7} id="tabpanel-7" aria-labelledby="tab-7">
            {tabValue === 7 && <LessonChangeRequestsPage />}
          </Box>
          <Box hidden={tabValue !== 8} id="tabpanel-8" aria-labelledby="tab-8">
            {tabValue === 8 && <CourseProgressDashboard />}
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default AdminDashboard;
